import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'b2chat-theme-mode';
type ThemeMode = 'light' | 'dark';

type ThemeModeContextValue = { mode: ThemeMode; toggle: () => void };

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  toggle: noop,
});

export const ThemeModeProvider: FC<
  PropsWithChildren<{ initialMode?: ThemeMode }>
> = ({ children, initialMode }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (initialMode) return initialMode;
    // Validate the stored value rather than blindly casting — a stale/corrupt
    // entry (e.g. 'auto') would otherwise reach MUI's palette.type.
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  });

  const toggle = useCallback(() => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => useContext(ThemeModeContext);
